import { siteStrings } from './site-strings';

export type FeatureTimerOptions = {
    isEligible: () => boolean;
    onElapsed: () => void;
    setTimeout: (callback: () => void, delayMilliseconds: number) => number;
    clearTimeout: (timeoutId: number) => void;
};

export type HomepagePlaybackControlPresentation = {
    isPlaying: boolean;
    label: string;
};

/** Defines the initial automatic homepage playback state. */
export const defaultHomepagePlaybackPlaying = true;

/** Maps automatic homepage playback state to its accessible toggle presentation. */
export function getHomepagePlaybackControlPresentation(isPlaying: boolean): HomepagePlaybackControlPresentation {
    return {
        isPlaying,
        label: isPlaying ? siteStrings.pauseHomepagePlayback : siteStrings.resumeHomepagePlayback
    };
}

export class AutomaticFeatureTimer {
    private timeoutId: number | null = null;

    public constructor(private readonly options: FeatureTimerOptions) {
    }

    public schedule(delayMilliseconds: number): void {
        this.cancel();
        if (!this.options.isEligible()) return;

        this.timeoutId = this.options.setTimeout(() => {
            this.timeoutId = null;
            if (this.options.isEligible()) this.options.onElapsed();
        }, delayMilliseconds);
    }

    public cancel(): void {
        if (this.timeoutId == null) return;

        this.options.clearTimeout(this.timeoutId);
        this.timeoutId = null;
    }
}

export type PausableDelayOptions = {
    setTimeout: (callback: () => void, delayMilliseconds: number) => number;
    clearTimeout: (timeoutId: number) => void;
    getTimestamp: () => number;
};

/** Runs a resettable delay while preserving its remaining duration across pauses. */
export class PausableDelay {
    private timeoutId: number | null = null;
    private remainingMilliseconds: number;
    private startedAt = 0;
    private started = false;
    private paused = false;

    public constructor(
        private readonly durationMilliseconds: number,
        private readonly onElapsed: () => void,
        private readonly options: PausableDelayOptions) {
        this.remainingMilliseconds = durationMilliseconds;
    }

    public start(paused = false): void {
        if (this.started) return;

        this.started = true;
        this.paused = paused;
        if (!paused) this.schedule();
    }

    public reset(): void {
        if (!this.started) return;

        this.clearScheduledTimeout();
        this.remainingMilliseconds = this.durationMilliseconds;
        if (!this.paused) this.schedule();
    }

    public pause(): void {
        if (!this.started || this.paused) return;

        this.paused = true;
        if (this.timeoutId == null) return;

        this.remainingMilliseconds = Math.max(
            0,
            this.remainingMilliseconds - (this.options.getTimestamp() - this.startedAt));
        this.clearScheduledTimeout();
    }

    public resume(): void {
        if (!this.started || !this.paused) return;

        this.paused = false;
        this.schedule();
    }

    public cancel(): void {
        this.clearScheduledTimeout();
        this.started = false;
    }

    private schedule(): void {
        this.startedAt = this.options.getTimestamp();
        this.timeoutId = this.options.setTimeout(() => {
            this.timeoutId = null;
            this.started = false;
            this.remainingMilliseconds = 0;
            this.onElapsed();
        }, this.remainingMilliseconds);
    }

    private clearScheduledTimeout(): void {
        if (this.timeoutId == null) return;

        this.options.clearTimeout(this.timeoutId);
        this.timeoutId = null;
    }
}

export class ExclusiveAsyncTask {
    private active = false;

    public get isActive(): boolean {
        return this.active;
    }

    public async tryRun(operation: () => Promise<unknown>): Promise<boolean> {
        if (this.active) return false;

        this.active = true;
        try {
            await operation();
            return true;
        } finally {
            this.active = false;
        }
    }
}

type RefreshCycleOptions<TConfig, TCollection> = {
    allowHidden: boolean;
    isHidden: () => boolean;
    loadConfig: () => Promise<TConfig>;
    getCollectionSlug: (config: TConfig) => string | null;
    loadCollection: (slug: string) => Promise<TCollection>;
    applyState: (config: TConfig, collection: TCollection | null) => void | Promise<void>;
};

export async function runRefreshCycle<TConfig, TCollection>(
    options: RefreshCycleOptions<TConfig, TCollection>): Promise<boolean> {
    const nextConfig = await options.loadConfig();
    if (!options.allowHidden && options.isHidden()) return false;

    const collectionSlug = options.getCollectionSlug(nextConfig);
    const nextCollection = collectionSlug == null
        ? null
        : await options.loadCollection(collectionSlug);
    await options.applyState(nextConfig, nextCollection);
    return true;
}

type CollectionRevision = {
    lastModifiedUtc: string;
    images: readonly unknown[];
};

export function hasCollectionChanged(
    currentCollection: CollectionRevision | null,
    nextCollection: CollectionRevision): boolean {
    return currentCollection == null
        || nextCollection.lastModifiedUtc !== currentCollection.lastModifiedUtc
        || nextCollection.images.length !== currentCollection.images.length;
}
